import axios from "axios";
import { API_BASE } from "../utils/apiConfig";

const BASE_URL = API_BASE + "/auth";

export const registerUser = async (userData) => {
    const response = await axios.post(`${BASE_URL}/register`, userData, {
        skipAuth: true // Skip token refresh logic for register endpoint
    });
    return response.data;
};

export const loginUser = async ({email, password}) => {
    // withCredentials: true ensures cookies (including refresh token) are sent/received
    // skipAuth: true prevents axios interceptor from trying to refresh token on 401 errors
    // (login is a public endpoint - 401 means wrong credentials, not expired token)
    const response = await axios.post(`${BASE_URL}/login`, {
        email,
        password,
    }, {
        withCredentials: true, // Important: allows cookies to be sent/received
        skipAuth: true // Skip token refresh logic for login endpoint
    });
    return response.data;
};

export const requestPasswordReset = async (email) => {
    const response = await axios.post(`${BASE_URL}/forgot-password`, { email }, {
        skipAuth: true // Skip token refresh logic for public endpoint
    });
    return response.data;
};

export const resetPassword = async (token, newPassword) => {
    const response = await axios.post(`${BASE_URL}/reset-password`, { token, newPassword }, {
        skipAuth: true // Skip token refresh logic for public endpoint
    });
    return response.data;
};

export const verifyEmail = async (token) => {
    const response = await axios.post(`${BASE_URL}/verify`, { token }, {
        skipAuth: true // Skip token refresh logic for public endpoint
    });
    return response.data;
};

export const resendVerification = async (email) => {
    const response = await axios.post(`${BASE_URL}/resend-verification`, { email }, {
        skipAuth: true // Skip token refresh logic for public endpoint
    });
    return response.data;
};

export const googleSignIn = async (idToken) => {
    // withCredentials: true ensures cookies (including refresh token) are sent/received
    // Backend expects only idToken (not accessToken)
    // skipAuth: true prevents axios interceptor from trying to refresh token on 401 errors
    const response = await axios.post(`${BASE_URL}/google-login`, {
        idToken
    }, {
        withCredentials: true, // Important: allows cookies to be sent/received
        skipAuth: true // Skip token refresh logic for login endpoint
    });
    return response.data;
};

/**
 * Refresh access token using refresh token from HTTP-only cookie
 * The refresh token is automatically sent by the browser as a cookie (HttpOnly)
 * @returns {Promise} Response with new access token or error info
 */
/**
 * Refresh access token using refresh token from HTTP-only cookie
 * The refresh token is automatically sent by the browser as a cookie (HttpOnly)
 * 
 * NOTE: 401 errors in console are EXPECTED when:
 * - User hasn't logged in yet (no refresh token cookie)
 * - Refresh token expired
 * - User logged out
 * 
 * These are handled gracefully and don't indicate a problem.
 * 
 * @returns {Promise} Response with new access token or error info
 */
export const refreshAccessToken = async () => {
    try {
        // No need to send refreshToken in body - it's in the HTTP-only cookie
        // Browser automatically sends the cookie when withCredentials: true
        const response = await axios.post(`${BASE_URL}/refresh`, {}, {
            withCredentials: true, // Important: sends cookies automatically
            // Don't treat 401 as error (expected when no refresh token cookie)
            validateStatus: (status) => status === 200 || status === 401
        });
        
        // If 401, return failure response (expected when no refresh token cookie)
        if (response.status === 401) {
            return { 
                success: false, 
                message: "No valid refresh token available" 
            };
        }
        
        return response.data;
    } catch (error) {
        // Handle network errors or other unexpected errors
        if (error.response?.status === 401) {
            // Already handled by validateStatus, but just in case
            return { 
                success: false, 
                message: "No valid refresh token available" 
            };
        }
        // For other errors, rethrow
        throw error;
    }
};

/**
 * Get authenticated user's profile information
 * Fetches basic user info (id, firstName, lastName, email, role) from backend
 * This endpoint requires authentication - Axios interceptor automatically attaches Authorization header
 * @returns {Promise<Object>} User profile object with id, firstName, lastName, email, role
 */
export const getMe = async () => {
    try {
        // Axios interceptor automatically attaches Authorization header from tokenRef
        // withCredentials is set globally in request interceptor for cookie support
        const response = await axios.get(`${BASE_URL}/me`);
        return response.data;
    } catch (error) {
        console.error("Error fetching user profile:", error);
        throw error;
    }
};

/**
 * Logout user - invalidates refresh token and clears HTTP-only cookie
 * Note: Backend requires authentication (uses getCurrentUser()), so this will return 401
 * if called without a valid access token. This is handled gracefully in AuthContext.
 * @returns {Promise} Response from logout endpoint
 */
export const logoutUser = async () => {
    try {
        // Call backend logout endpoint to:
        // 1. Invalidate refresh token in database
        // 2. Clear HTTP-only cookie (backend sends Set-Cookie with Max-Age=0)
        // Backend endpoint: POST /auth/logout (requires authentication)
        const response = await axios.post(`${BASE_URL}/logout`, {}, {
            withCredentials: true // Important: sends cookies so backend can clear them
        });
        return response.data;
    } catch (error) {
        // Backend may return 401 if:
        // - Access token is expired/invalid
        // - User is not authenticated
        // This is OK - we still clear frontend state regardless
        // (e.g., if backend is down or token expired, user should still be logged out locally)
        if (error.response?.status !== 401) {
            console.error("Logout error:", error);
        }
        return { success: false, message: error.response?.data?.message || "Logout failed" };
    }
};